import { Notification } from '@/app/plugins/notifications/Notification';
import { useViewAsOwner } from '@/shared/composition';
import { useUserStore } from '@/shared/store/useUserStore';
import {
  ActionsSource,
  ActualSource,
  AuthRetry,
  awareOf,
  Barrier,
  BoolSource,
  FetchClient,
  Fork,
  FunctionSource,
  GuestAware,
  GuestAwareApplied,
  Keycloak,
  KeycloakType,
  LocalStorageRecord,
  Memo,
  MergedRecursive,
  NormalizedApiUrl,
  PathEmpty,
  Patron,
  PatronOnce,
  PrimitiveString,
  Private,
  RecordOf,
  source,
  SourceChain,
  SourceEmpty,
  SourceOnce,
  SourceSync,
  Tick,
  UrlParameterExisted,
} from '@synergy/sf-commons';
import { v4 as uuidv4 } from 'uuid';

// Функция для отладки источников данных
(window as any).debug = (name: string, record: Record<string, GuestAware>) => {
  // eslint-disable-next-line no-console
  new GuestAwareApplied(new RecordOf(record), r => ({ name, ...r })).receiving(new Patron(console.table));
};

export const defaultLimitSrc = awareOf(10);
export const errorSource = new SourceEmpty();
export const infoSource = new SourceEmpty();

errorSource.receiving(new Patron(new Notification('error')));
infoSource.receiving(new Patron(new Notification('success')));

export const keycloakSource = new SourceEmpty<KeycloakType>();
const tokenSource = new PathEmpty(keycloakSource, 'token');
export const keycloak = new Keycloak(keycloakSource);
const kcSync = new SourceSync(keycloakSource, {});

export const bitrixSiteUrl = new Memo(new NormalizedApiUrl(new LocalStorageRecord('api-bitrix-url', process.env.BITRIX_URL)));
export const serviceBaseUrl = new Memo(
  new GuestAwareApplied(new LocalStorageRecord<string>('api-svc-url', process.env.API_BASE_URL), url => url.replace(/\/api\/?$/, '')),
);

const hasOwnerEmailSrc = new UrlParameterExisted('ownerEmail');

// Обновление истёкщего токена
const authRefreshTimestampSrc = new SourceEmpty();
const needAuthRefreshSrc = new SourceEmpty();
const updatedKeycloakSource = keycloak.updateToken(needAuthRefreshSrc, errorSource);
updatedKeycloakSource.receiving(new Patron(() => {
  authRefreshTimestampSrc.receive(Date.now());
}));

// Клиент для сервиса Node.js
export const serviceHeaders = new Memo(new RecordOf({
  'Accept': awareOf('application/json'),
  'Content-Type': awareOf('application/json'),
  'x-source-lp': new Fork(hasOwnerEmailSrc, Boolean, awareOf('personalAreaPartnerLP'), awareOf('personalAreaClientLP')),
  'authorization': new SourceChain([
    new SourceOnce(tokenSource),
    // Важно оборачивать в PrimitiveString чтобы токен всегда уходил актуальный
    awareOf(new PrimitiveString(new FunctionSource(() => `Bearer ${kcSync.valueSync().token}`))),
  ]),
  // Тоже важно обернуть в PrimitiveString чтобы каждый запрос получил новый UUID
  'X-Request-ID': awareOf(new PrimitiveString(new FunctionSource(() => uuidv4()))),
}));
export const serviceSrc = new Private((source, errorsSrc, abortSrc) => {
  const localErrorSrc = new SourceEmpty();
  const theErrorSrc = errorsSrc ?? errorSource;
  localErrorSrc.receiving(new Patron(theErrorSrc));
  const theRequestSrc = new MergedRecursive([
    new RecordOf({
      baseUrl: serviceBaseUrl,
      headers: serviceHeaders,
    }),
    source,
  ]);

  const authResetActualSrc = new Barrier(
    authRefreshTimestampSrc,
    new BoolSource(localErrorSrc, theRequestSrc),
  );
  const authRetry = new AuthRetry(
    theRequestSrc,
    needAuthRefreshSrc,
    authResetActualSrc,
  );
  localErrorSrc.receiving(new Patron(authRetry.error()));
  return new FetchClient(
    new Tick(authRetry.request()),
    localErrorSrc,
    abortSrc,
  ).addRelatedDeps([localErrorSrc]);
});

export function AvailableUserEmail() {
  const { ownerEmail } = useViewAsOwner();
  const { getUserInfo } = useUserStore();
  return awareOf(ownerEmail.value || getUserInfo.userEmail);
}

// БУС для обмена JSON
const bitrixJsonHeaders = new Memo(new RecordOf({
  'Accept': awareOf('application/json'),
  'Content-Type': awareOf('application/json'),
  'Version': awareOf('1'),
  'authorization': new SourceChain([
    new SourceOnce(tokenSource),
    // Важно оборачивать в PrimitiveString чтобы токен всегда уходил актуальный
    awareOf(new PrimitiveString(new FunctionSource(() => `Bearer ${kcSync.valueSync().token}`))),
  ]),
}));
export const bitrixJsonSrc = new Private((source, errorsSrc, abortSrc) => {
  const localErrorSrc = new SourceEmpty();
  const theErrorSrc = errorsSrc ?? errorSource;
  localErrorSrc.receiving(new Patron(theErrorSrc));
  const theRequestSrc = new MergedRecursive([
    new RecordOf({
      baseUrl: bitrixSiteUrl,
      headers: bitrixJsonHeaders,
    }),
    ActualSource(source),
  ]);
  const authResetActualSrc = new Barrier(
    authRefreshTimestampSrc,
    new BoolSource(localErrorSrc, theRequestSrc),
  );
  const authRetry = new AuthRetry(
    theRequestSrc,
    needAuthRefreshSrc,
    authResetActualSrc,
  );
  localErrorSrc.receiving(new Patron(authRetry.error()));
  return new FetchClient(
    new Tick(authRetry.request()),
    localErrorSrc,
    abortSrc,
  ).addRelatedDeps([localErrorSrc]);
});

export const actionsSource = new ActionsSource([
  [
    'ask-bus',
    command => {
      return new Promise((resolve, reject) => {
        const error = source();
        error.receiving(new Patron(errorSource));
        error.receiving(new PatronOnce(reject));
        bitrixJsonSrc.get(command.args?.[0] ?? {}, error).receiving(new PatronOnce(v => {
          resolve(v);
        }));
      });
    },
  ],
]);
